import { Row } from '@tanstack/react-table';
import { MenuItemType } from '../../../../../components/PaginatedTable/PaginatedTable.types';
import { DocumentTabs, MappingSessionRecord } from '../../../../../types';
import { CatalogItemsHash } from '../../../../upload/UploadSource/UploadSource.type';

type CatalogTableRow = MappingSessionRecord & Record<'id', string>;
type CatalogPreviewData = { id?: number; targetFileId?: number };
type CatalogEditRow = {
  original: { title: string; version: number; id: number; catalogItems: CatalogItemsHash };
};

const noopMenuAction = () => undefined;

const generateMenuItems = (
  activeTab: DocumentTabs,
  selectedTab: DocumentTabs,
  handleDelete: (id: string) => void,
  handlePreviewDocument: (id: number, documentType: boolean) => void,
  handleEdit: (row: CatalogEditRow) => void,
  startAuthoringOnClick: (id: string, useLLM?: boolean) => void,
  handleAuthor: (id: string) => void,
  handleVerify: (id: string) => void,
  handleConvert: (id: number, title: string) => void,
  handleCopy: (row: Row<CatalogTableRow>) => void,
  viewSessionData: (row: Row<CatalogTableRow>) => void,
  handlePreviewData: (row: CatalogPreviewData) => void,
): MenuItemType<CatalogTableRow>[] => {
  const commonItems = (additionalItems: MenuItemType<CatalogTableRow>[] = []) => {
    const commonItemsOptions: MenuItemType<CatalogTableRow>[] = [
      ...additionalItems,
      {
        title: 'Delete',
        onClick: handleDelete,
        className: 'text-negative-50 hover:text-negative-100',
      },
    ];

    return commonItemsOptions;
  };

  switch (activeTab) {
    case DocumentTabs.TARGET:
      return [
        {
          title: 'Start Authoring',
          onClick: (id: string) => startAuthoringOnClick(id),
        },
        {
          title: 'View',
          onClick: (_id, row) => handlePreviewData(row?.original),
        },
        ...(selectedTab !== DocumentTabs.EXTERNAL
          ? [
              {
                title: 'Edit',
                onClick: (_id: string, row: Row<CatalogTableRow>) => {
                  handleEdit(row as CatalogEditRow);
                },
              },
              ...commonItems(),
            ]
          : []),
      ];
    case DocumentTabs.SOURCE:
      return [
        {
          title: 'View',
          onClick: (_id: string, row: Row<CatalogTableRow>) => {
            const sourceRow = row.original as CatalogTableRow & {
              isInteractive?: boolean;
              nonParsedCatalogId?: number;
              baseTargetFileId?: number;
            };
            const catalogId = sourceRow.isInteractive ? sourceRow.id : sourceRow.nonParsedCatalogId;
            const documentId = sourceRow.baseTargetFileId || catalogId;
            const documentType = sourceRow.baseTargetFileId ?? sourceRow.isInteractive;
            if (catalogId && documentId) {
              handlePreviewDocument(documentId, !!documentType);
            }
          },
        },
        {
          title: 'Edit',
          onClick: (_id: string, row: Row<CatalogTableRow>) => {
            handleEdit(row as CatalogEditRow);
          },
        },
        ...commonItems(),
      ];
    case DocumentTabs.IN_PROGRESS:
      return [
        {
          title: 'Author',
          onClick: handleAuthor,
        },
        {
          title: 'Verify',
          onClick: handleVerify,
        },
        {
          title: 'Copy',
          onClick: (_id, row) => handleCopy(row),
        },
        {
          title: 'Convert',
          onClick: (id, row) => {
            if (row.original.status !== 'Ready to Author') {
              handleConvert(+id, row.original.title || '');
            }
          },
        },
        {
          title: 'Edit',
          onClick: (_id: string, row: Row<CatalogTableRow>) => {
            handleEdit(row as CatalogEditRow);
          },
        },
        ...commonItems([
          {
            title: 'Doc Info',
            onClick: (_id, row) => viewSessionData(row),
          },
        ]),
      ];
    case DocumentTabs.COMPLETED:
      return [
        {
          title: 'View',
          onClick: noopMenuAction,
        },
        {
          title: 'Copy',
          onClick: (_id, row) => handleCopy(row),
        },

        ...commonItems(),
      ];
    default:
      return [];
  }
};

export default generateMenuItems;
